import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, AlertTitle, Backdrop,
    Box, Button, Card, CardContent, Chip, CircularProgress,
    IconButton, InputAdornment,
    Paper, Stack, TextField, Typography,
    ToggleButton, ToggleButtonGroup, Autocomplete, Checkbox, FormControlLabel,
    Dialog, DialogTitle, DialogContent, DialogActions,
    List, ListItemButton, Grid,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon, Send as SendIcon,
    Add as AddIcon, Delete as DeleteIcon,
    Mic as MicIcon, MicOff as MicOffIcon,
    Person as PersonIcon, Group as GroupIcon,
    OpenInNew as OpenInNewIcon, Assignment as AssignmentIcon,
    PictureAsPdf as PictureAsPdfIcon,
    ContentCopy as ContentCopyIcon, Search as SearchIcon,
    Edit as EditIcon, Close as CloseIcon,
    DriveFileRenameOutline as DriveFileRenameOutlineIcon,
    PersonSearch as PersonSearchIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axiosInstance from '../../../axiosInstance';
import { initializeDB } from '../../../config/db';
import {
    JOB_STORE, FALL_ARREST_RESCUE_PLAN_STORE,
    STATUS_DRAFT, STATUS_SUBMITTED,
} from '../../../config/constants';
import { generateUUID } from '../../../utils/uuid';
import useDictation from '../../../hooks/useDictation';
import fallArrestRescuePlanService from '../../../services/fallArrestRescuePlanService';
import SignatureModal from '../../jha/SignatureModal';
import SelectEmployeesAndTempsModal from '../../jha/SelectEmployeesAndTempsModal';
import SelectFromWeeklyRosterModal from '../../jha/SelectFromWeeklyRosterModal';
import PdfPreviewModal from '../../jha/pdf/PdfPreviewModal';

// Swal-toast по умолчанию имеет z-index 1060 — ниже чем MUI AppBar (1100),
// поэтому toast перекрывается шапкой. Поднимаем выше всех layer'ов.
const TOAST_DEFAULTS = {
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    didOpen: () => {
        document.querySelectorAll('.swal2-container').forEach((c) => {
            c.style.zIndex = '13000';
        });
    },
};

const EQUIPMENT_LOCATIONS = [
    { key: 'within100',     label: "Within 100'" },
    { key: 'from100to300',  label: "100' - 300'" },
    { key: 'over300',       label: "300' or more" },
];

const RESCUE_EQUIPMENT_LIST = [
    { key: 'mewps',                   label: 'MEWPs' },
    { key: 'ladder',                  label: 'Ladder' },
    { key: 'rescuePositioningDevice', label: 'Rescue Positioning Device (RPD)' },
    { key: 'manualEntry',             label: 'Manual Entry', hint: 'Requires Authorization from Safety Professional' },
];

// ──────────────────────────────────────────────────────────────────────────
//  Section — Card-паттерн как в JhaStepOne (subtitle2 заголовок внутри CardContent).
// ──────────────────────────────────────────────────────────────────────────
const Section = ({ title, children }) => (
    <Card elevation={1} sx={{ mt: 1, mb: 1 }}>
        <CardContent sx={{ py: 1.75 }}>
            <Stack spacing={1.25}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title}</Typography>
                {children}
            </Stack>
        </CardContent>
    </Card>
);

// ──────────────────────────────────────────────────────────────────────────
//  TextFieldWithMic — textarea с кнопкой voice-to-text (useDictation hook)
//
//  Singleton useDictation() — один активный recognition на приложение.
//  В onResult читаем актуальное value через ref (closure от render устаревает
//  на втором transcript-event'е, поэтому использовать `value` из props нельзя).
// ──────────────────────────────────────────────────────────────────────────
const TextFieldWithMic = ({ value, onChange, label, placeholder, minRows = 2, maxRows = 12, rows, singleLine = false, autoGrow = false, ...rest }) => {
    const { isSupported, isListening, start, stop } = useDictation();
    const [activeField, setActiveField] = useState(false);

    // Refs для актуальных value/onChange внутри onResult — closure-safe.
    const valueRef = useRef(value);
    const onChangeRef = useRef(onChange);
    useEffect(() => { valueRef.current = value; }, [value]);
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

    const toggleDictation = () => {
        if (isListening && activeField) {
            stop();
            setActiveField(false);
            return;
        }
        setActiveField(true);
        start({
            onResult: (transcript) => {
                const current = valueRef.current || '';
                const needsSpace = current && !current.endsWith(' ');
                const next = current + (needsSpace ? ' ' : '') + transcript;
                onChangeRef.current(next);
            },
            onEnd: () => setActiveField(false),
            onError: () => setActiveField(false),
        });
    };

    const listening = isListening && activeField;

    // autoGrow = compact single-line высота, grow при многострочном тексте.
    // Реализация: multiline + minRows=1 + maxRows=10 + minimal padding в root.
    const multiProps = singleLine
        ? {}
        : autoGrow
            ? { multiline: true, minRows: 1, maxRows: 10 }
            : { multiline: true, ...(rows ? { rows } : { minRows, maxRows }) };

    // Для multiline (autoGrow или явный multiline) — mic должен быть всегда в
    // top-right (чтобы при росте textarea не съезжал вниз). Для single-line —
    // центрируется автоматически.
    const micAlignTop = !singleLine;

    return (
        <TextField size="small"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            label={label}
            placeholder={placeholder}
            {...multiProps}
            fullWidth
            InputProps={{
                endAdornment: isSupported && (
                    <InputAdornment position="end" sx={micAlignTop ? { alignSelf: 'flex-start', mt: '6px' } : {}}>
                        <IconButton
                            size="small"
                            color={listening ? 'error' : 'default'}
                            onClick={toggleDictation}
                            title={listening ? 'Stop dictation' : 'Start dictation'}
                        >
                            {listening ? <MicOffIcon /> : <MicIcon />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
            {...rest}
        />
    );
};

// ──────────────────────────────────────────────────────────────────────────
//  Main component
// ──────────────────────────────────────────────────────────────────────────
export default function FallArrestRescuePlanForm() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const editingUuid = searchParams.get('uuid');
    // Pre-fill через query params — используется ссылкой "Open Fall Arrest Rescue Plan"
    // из JHA banner: /safety/forms/fall-arrest-rescue-plan/form?jobCode=X&planDate=Y&jhaLocalId=Z
    const prefillJobCode    = searchParams.get('jobCode')    || '';
    const prefillPlanDate   = searchParams.get('planDate')   || '';
    // jhaLocalId — точная связь с конкретным JHA. Один JHA → один FARP.
    // У джоба за день может быть несколько JHA для разных работ; jhaLocalId
    // отличает их. Пустой для standalone FARP (открыт из меню).
    const prefillJhaLocalId = searchParams.get('jhaLocalId') || '';
    // returnTo — куда вернуться после Back / Submit / Cancel.
    const returnTo          = searchParams.get('returnTo') || '/safety/forms/fall-arrest-rescue-plan';
    const isFromJha         = returnTo.startsWith('/jha');

    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userPhone = localStorage.getItem('employeePhone') || '';
    const userId = localStorage.getItem('employeeId') || null;

    // ─── State ─────────────────────────────────────────────────────────
    const [form, setForm] = useState(() => ({
        uuid: editingUuid || generateUUID(),
        status: STATUS_DRAFT,
        jhaLocalId: prefillJhaLocalId || null,
        jobCode: prefillJobCode, jobName: '', jobLocation: '', jobDescription: '', customerName: '',
        planDate: prefillPlanDate || dayjs().format('YYYY-MM-DD'),

        rescuers: [],
        // Default Competent Person = current user (юзер, заполняющий форму)
        competentPerson: { empId: userId, name: userFullName },
        emergencyContact: { name: userFullName, phone: userPhone },

        methodOfContact: fallArrestRescuePlanService.getDefaultMethodOfContact(),
        rescueEquipment: fallArrestRescuePlanService.getDefaultRescueEquipment(),
        equipmentLocation: fallArrestRescuePlanService.getDefaultEquipmentLocation(),

        anchorPoint: '', landingArea: '', obstructionsHazards: '',
        checklist: fallArrestRescuePlanService.getDefaultChecklist(),
        preWorkTasks: [],
        responseProcedure: fallArrestRescuePlanService.getDefaultResponseProcedure(),

        submitterName: userFullName,
        submitterRole: '',
        submitterSignature: '',
        submitterSignatureDate: '',

        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    }));

    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Modals
    const [signatureOpen, setSignatureOpen] = useState(false);
    const [rescuersModalOpen, setRescuersModalOpen] = useState(false);
    const [rescuersRosterOpen, setRescuersRosterOpen] = useState(false);
    const [competentModalOpen, setCompetentModalOpen] = useState(false);
    const [emergencyModalOpen, setEmergencyModalOpen] = useState(false);
    const [submitterModalOpen, setSubmitterModalOpen] = useState(false);

    // Existing submitted FARP for the same (jobCode + planDate) — banner.
    const [existingSubmitted, setExistingSubmitted] = useState(null);

    // Copy-from-previous picker modal
    const [copyModalOpen, setCopyModalOpen] = useState(false);
    const [copySources, setCopySources] = useState([]);
    const [copySearch, setCopySearch] = useState('');

    // PDF preview modal — для existingSubmitted banner
    const [pdfModalOpen, setPdfModalOpen] = useState(false);
    const [pdfBlob, setPdfBlob] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);

    // Pre-fill phone.numbers с phone текущего юзера если массив пустой.
    useEffect(() => {
        if (!userPhone) return;
        setForm((prev) => {
            const existing = prev.methodOfContact?.phone?.numbers || [];
            if (existing.length > 0) return prev;
            return {
                ...prev,
                methodOfContact: {
                    ...prev.methodOfContact,
                    phone: { ...prev.methodOfContact.phone, numbers: [userPhone] },
                },
            };
        });
    }, [userPhone]);

    // ─── Load jobs + draft (or existing draft by job+date) + submitted check ─
    useEffect(() => {
        (async () => {
            try {
                const db = await initializeDB();

                // jobs
                let allJobs = [];
                if (db.objectStoreNames.contains(JOB_STORE)) {
                    allJobs = await db.transaction(JOB_STORE, 'readonly').objectStore(JOB_STORE).getAll();
                    setJobs(allJobs || []);
                }

                // Priority A: explicit ?uuid → load that draft
                if (editingUuid) {
                    const draft = await fallArrestRescuePlanService.loadByUuid(editingUuid);
                    if (draft) {
                        setForm((prev) => ({ ...prev, ...draft }));
                        // Still check for existing submitted FARP for the same job+date
                        // (so user knows if there's already a parallel one).
                        if (draft.jobCode && draft.planDate) {
                            const { exists, plan } = await fallArrestRescuePlanService.checkExisting(
                                draft.jobCode, draft.planDate
                            );
                            if (exists && plan) setExistingSubmitted(plan);
                        }
                        return;
                    }
                }

                // Priority B: URL pre-fill from JHA banner. First try to find an
                // existing DRAFT for the same (jobCode + planDate) — prevents
                // duplicates if user clicks "Open Fall Arrest Rescue Plan" twice.
                const targetDate = prefillPlanDate || dayjs().format('YYYY-MM-DD');
                if (prefillJobCode) {
                    const existingDraft = await fallArrestRescuePlanService.findDraftByJobAndDate(
                        prefillJobCode, targetDate
                    );
                    if (existingDraft) {
                        // Open the existing draft instead of creating a new one.
                        setForm((prev) => ({ ...prev, ...existingDraft }));
                    } else if (allJobs.length > 0) {
                        // No draft yet — pre-fill from JOB_STORE (same as handleJobSelect)
                        const j = allJobs.find(x => (x.Job || x.JobCode) === prefillJobCode);
                        if (j) {
                            setForm((prev) => ({
                                ...prev,
                                jobCode:        j.Job || j.JobCode || prefillJobCode,
                                jobName:        j.JobName || prev.jobName || '',
                                customerName:   j.Customer || prev.customerName || '',
                                jobLocation:    j.location || j.Location || prev.jobLocation || '',
                                jobDescription: j.workDescription || j.WorkDescription || prev.jobDescription || '',
                            }));
                        }
                    }

                    // Always check whether a SUBMITTED FARP already exists for this
                    // job+date (online — falls back gracefully if offline).
                    const { exists, plan } = await fallArrestRescuePlanService.checkExisting(
                        prefillJobCode, targetDate
                    );
                    if (exists && plan) setExistingSubmitted(plan);
                }
            } catch (e) {
                console.error('Load error:', e);
            } finally {
                setLoading(false);
            }
        })();
    }, [editingUuid, prefillJobCode, prefillPlanDate, prefillJhaLocalId]);

    // ─── Copy-from-previous picker ─────────────────────────────────
    // Open modal: load submitted FARP from IDB, prioritize same-job at top.
    const openCopyPicker = async () => {
        const db = await initializeDB();
        if (!db.objectStoreNames.contains(FALL_ARREST_RESCUE_PLAN_STORE)) {
            setCopySources([]);
            setCopyModalOpen(true);
            return;
        }
        const all = await db.getAll(FALL_ARREST_RESCUE_PLAN_STORE);
        const submitted = (all || [])
            .filter(p => p.status === 'submitted' && p.uuid !== form.uuid)
            .sort((a, b) => new Date(b.submittedAt || b.updatedAt || 0) - new Date(a.submittedAt || a.updatedAt || 0));
        setCopySources(submitted);
        setCopySearch('');
        setCopyModalOpen(true);
    };

    const applyCopyFrom = (src) => {
        // Patch текущей формы. Сохраняем СВОЙ uuid/status/planDate/submitter/jhaLocalId.
        setForm((prev) => ({
            ...prev,
            jobCode:             src.jobCode || prev.jobCode,
            jobName:             src.jobName || prev.jobName,
            jobLocation:         src.jobLocation || prev.jobLocation,
            jobDescription:      src.jobDescription || prev.jobDescription,
            customerName:        src.customerName || prev.customerName,
            rescuers:            src.rescuers || prev.rescuers,
            competentPerson:     src.competentPerson || prev.competentPerson,
            emergencyContact:    src.emergencyContact || prev.emergencyContact,
            methodOfContact:     src.methodOfContact || prev.methodOfContact,
            rescueEquipment:     src.rescueEquipment || prev.rescueEquipment,
            equipmentLocation:   src.equipmentLocation || prev.equipmentLocation,
            anchorPoint:         src.anchorPoint || prev.anchorPoint,
            landingArea:         src.landingArea || prev.landingArea,
            obstructionsHazards: src.obstructionsHazards || prev.obstructionsHazards,
            checklist:           src.checklist || prev.checklist,
            preWorkTasks:        src.preWorkTasks || prev.preWorkTasks,
            responseProcedure:   src.responseProcedure || prev.responseProcedure,
            // НЕ копируем: uuid, status, planDate, jhaLocalId, submitter*, dates
        }));
        setCopyModalOpen(false);
        Swal.fire({
            ...TOAST_DEFAULTS,
            icon: 'success',
            title: 'Copied from ' + (src.jobCode || src.planDate || 'previous'),
            timer: 2000,
        });
    };

    const filteredCopySources = useMemo(() => {
        const q = (copySearch || '').trim().toLowerCase();
        const currentJob = form.jobCode;
        // Same-job сверху
        const ranked = copySources.map(s => ({ s, score: s.jobCode === currentJob ? 0 : 1 }));
        ranked.sort((a, b) => a.score - b.score);
        if (!q) return ranked.map(x => x.s);
        return ranked.map(x => x.s).filter(s => {
            const hay = [s.jobCode, s.jobName, s.jobLocation, s.planDate, s.submitterName]
                .filter(Boolean).join(' ').toLowerCase();
            return hay.includes(q);
        });
    }, [copySources, copySearch, form.jobCode]);

    // ─── PDF view of existing submitted FARP ──────────────────────────
    // Открываем в PdfPreviewModal — iframe на desktop, кнопки Open/Download на iOS.
    // Тот же UX-паттерн что в JHA list / FARP list.
    const viewExistingPdf = async () => {
        if (!existingSubmitted?.id) return;
        setPdfBlob(null);
        setPdfLoading(true);
        setPdfModalOpen(true);
        try {
            const resp = await axiosInstance.get(`/fall-arrest-rescue-plan/${existingSubmitted.id}/pdf`, {
                responseType: 'blob',
            });
            setPdfBlob(new Blob([resp.data], { type: 'application/pdf' }));
        } catch (e) {
            setPdfModalOpen(false);
            Swal.fire('Error', 'Failed to load PDF: ' + (e.message || 'unknown error'), 'error');
        } finally {
            setPdfLoading(false);
        }
    };

    const handleClosePdfModal = () => {
        setPdfModalOpen(false);
        setPdfBlob(null);
    };

    // ─── Auto-save (debounced 800ms) ────────────────────────────────────
    const saveTimer = useRef(null);
    useEffect(() => {
        if (loading || form.status === STATUS_SUBMITTED) return;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            fallArrestRescuePlanService.saveDraft(form).catch((e) => console.error('Auto-save error:', e));
        }, 800);
        return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    }, [form, loading]);

    // ─── Field helpers ──────────────────────────────────────────────────
    const updateField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

    const updateNested = (key, patch) => setForm((p) => ({ ...p, [key]: { ...p[key], ...patch } }));

    // Job picker — IDB Job entity: Job, JobName, workDescription, Customer, location/Location.
    // workDescription тянется из внешней [DH_Griffin].[dbo].[Job].WorkDescription через syncJobs().
    const handleJobSelect = (job) => {
        if (!job) {
            setForm((p) => ({
                ...p, jobCode: '', jobName: '', customerName: '', jobLocation: '', jobDescription: '',
            }));
            return;
        }
        setForm((p) => ({
            ...p,
            jobCode:        job.Job || job.JobCode || '',
            jobName:        job.JobName || '',
            customerName:   job.Customer || '',
            jobLocation:    job.location || job.Location || '',
            jobDescription: job.workDescription || job.WorkDescription || '',
        }));
    };

    // Rescuers — filter out persons w/o FullName ('undefined' chips bug).
    // Используется и для SelectEmployeesAndTempsModal, и для SelectFromWeeklyRosterModal.
    const handleRescuersSelected = (people) => {
        const rescuers = (people || [])
            .filter(p => p?.FullName && p.FullName !== 'Unknown')
            .map((p) => ({
                empId: p.employeeId || p.id || null,
                name:  p.FullName,
                source: p._type === 'temp' ? 'temp' : 'dhgc',
            }));
        updateField('rescuers', rescuers);
        setRescuersModalOpen(false);
        setRescuersRosterOpen(false);
    };

    // Competent person (single)
    const handleCompetentSelected = (people) => {
        const p = (people || []).find(x => x?.FullName && x.FullName !== 'Unknown');
        if (p) {
            updateField('competentPerson', {
                empId: p.employeeId || p.id || null,
                name:  p.FullName,
            });
        }
        setCompetentModalOpen(false);
    };

    // Emergency Contact — pick from employees → autofill Name + Phone
    const handleEmergencyContactSelected = (people) => {
        const p = (people || []).find(x => x?.FullName && x.FullName !== 'Unknown');
        if (p) {
            updateField('emergencyContact', {
                name:  p.FullName,
                phone: p.Phone || p.phone || '',
            });
        }
        setEmergencyModalOpen(false);
    };

    // Submitter — change который заполняет форму
    const handleSubmitterSelected = (people) => {
        const p = (people || []).find(x => x?.FullName && x.FullName !== 'Unknown');
        if (p) {
            updateField('submitterName', p.FullName);
        }
        setSubmitterModalOpen(false);
    };

    // Method of Contact toggles. При включении radio/phone — если массив пуст,
    // сразу добавляем одну пустую строку (для phone — userPhone текущего юзера).
    const toggleMethod = (key) => {
        const current = form.methodOfContact?.[key];
        if (typeof current === 'boolean') {
            updateNested('methodOfContact', { [key]: !current });
            return;
        }
        const willEnable = !current?.enabled;
        let updated = { ...current, enabled: willEnable };
        if (willEnable) {
            if (key === 'radio' && (!updated.channels || updated.channels.length === 0)) {
                updated.channels = [''];
            }
            if (key === 'phone' && (!updated.numbers || updated.numbers.length === 0)) {
                updated.numbers = [userPhone || ''];
            }
        }
        updateNested('methodOfContact', { [key]: updated });
    };

    const updateMethodDetail = (key, field, value) => {
        const current = form.methodOfContact?.[key] || {};
        updateNested('methodOfContact', { [key]: { ...current, [field]: value } });
    };

    // Rescue Equipment
    const toggleEquipment = (key) => {
        updateNested('rescueEquipment', { [key]: !form.rescueEquipment?.[key] });
    };

    // Equipment Location
    const toggleLocation = (key) => {
        updateNested('equipmentLocation', { [key]: !form.equipmentLocation?.[key] });
    };

    // Checklist (Yes/No + comment)
    const updateChecklistAnswer = (id, answer) => {
        setForm((p) => ({
            ...p,
            checklist: (p.checklist || []).map((it) => it.id === id ? { ...it, answer } : it),
        }));
    };
    const updateChecklistComment = (id, comment) => {
        setForm((p) => ({
            ...p,
            checklist: (p.checklist || []).map((it) => it.id === id ? { ...it, comment } : it),
        }));
    };

    // Pre-Work Tasks / Rescue Prep (dynamic list)
    const addPreWorkTask = () => {
        const list = form.preWorkTasks || [];
        updateField('preWorkTasks', [...list, { order: list.length + 1, text: '', isDefault: false }]);
    };
    const updatePreWorkTask = (idx, text) => {
        const list = [...(form.preWorkTasks || [])];
        list[idx] = { ...list[idx], text };
        updateField('preWorkTasks', list);
    };
    const removePreWorkTask = (idx) => {
        const list = (form.preWorkTasks || []).filter((_, i) => i !== idx)
            .map((t, i) => ({ ...t, order: i + 1 }));
        updateField('preWorkTasks', list);
    };

    // Response Procedure (2 default seeded + dynamic)
    const addResponseStep = () => {
        const list = form.responseProcedure || [];
        updateField('responseProcedure', [
            ...list,
            { order: list.length + 1, text: '', isDefault: false },
        ]);
    };
    const updateResponseStep = (idx, text) => {
        const list = [...(form.responseProcedure || [])];
        list[idx] = { ...list[idx], text };
        updateField('responseProcedure', list);
    };
    const removeResponseStep = (idx) => {
        const list = (form.responseProcedure || []).filter((_, i) => i !== idx)
            .map((t, i) => ({ ...t, order: i + 1 }));
        updateField('responseProcedure', list);
    };

    // Signature
    const handleSignatureSave = (dataUrl) => {
        updateField('submitterSignature', dataUrl);
        updateField('submitterSignatureDate', new Date().toISOString());
        setSignatureOpen(false);
    };

    // ─── Submit ─────────────────────────────────────────────────────────
    const validate = () => {
        const errors = [];
        if (!form.jobCode) errors.push('Job is required');
        if (!form.planDate) errors.push('Plan Date is required');
        if (!form.submitterName) errors.push('Submitter name is required');
        if (!form.submitterSignature) errors.push('Submitter signature is required');
        if (!form.rescuers || form.rescuers.length === 0) errors.push('At least one rescuer must be selected');
        return errors;
    };

    const handleSubmit = async () => {
        const errors = validate();
        if (errors.length > 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Cannot submit yet',
                html: '<ul style="text-align:left;">' + errors.map((e) => `<li>${e}</li>`).join('') + '</ul>',
            });
            return;
        }

        const confirm = await Swal.fire({
            title: 'Submit Fall Arrest Rescue Plan?',
            text: 'Once submitted, the form will be uploaded to Job Documents and notifications will be sent.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, submit',
            confirmButtonColor: '#1565c0',
        });
        if (!confirm.isConfirmed) return;

        setSubmitting(true);
        // Отменяем pending auto-save: если submit пройдёт за <800ms, debounced
        // saveDraft перезатёр бы удалённую запись, восстановив её как draft.
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
            saveTimer.current = null;
        }
        try {
            const payload = { ...form, status: STATUS_SUBMITTED };
            const resp = await fallArrestRescuePlanService.submit(payload);

            // Three possible outcomes from service.submit():
            //   1) resp.success === true            → server accepted
            //   2) resp.queued === true             → offline, saved as pending_submit
            //   3) resp.success === false (no queue)→ server rejected (validation/4xx/5xx)
            if (resp?.success) {
                // service.submit() уже сохранил submitted-mirror в IDB.
                // Локально помечаем форму как submitted, чтобы auto-save
                // guard блокировал любые поздние saves.
                setForm((prev) => ({ ...prev, status: STATUS_SUBMITTED }));
                await Swal.fire({
                    icon: 'success',
                    title: 'Submitted',
                    text: isFromJha
                        ? 'Fall Arrest Rescue Plan was submitted. Returning to JHA…'
                        : 'Fall Arrest Rescue Plan was submitted successfully.',
                    timer: 1500,
                    showConfirmButton: false,
                });
                navigate(returnTo);
            } else if (resp?.queued) {
                // Offline submit — record kept locally as pending_submit, will
                // auto-sync when online (App.jsx hooks `online` event).
                await Swal.fire({
                    icon: 'info',
                    title: 'Saved offline',
                    html: 'You appear to be offline. The form has been saved and<br>'
                        + '<strong>will be submitted automatically when you reconnect.</strong>',
                    confirmButtonText: 'OK',
                });
                navigate(returnTo);
            } else {
                Swal.fire('Submit failed', resp?.message || 'Server rejected the form', 'error');
            }
        } catch (e) {
            // Should not happen — service.submit() handles network errors via queue.
            // Catches only critical IDB / unexpected exceptions.
            Swal.fire('Error', e.response?.data?.message || e.message || 'Submit failed', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // ─── Memoized job options ───────────────────────────────────────────
    const jobOptions = useMemo(() => jobs || [], [jobs]);
    const selectedJobOption = useMemo(() => {
        if (!form.jobCode) return null;
        return jobOptions.find((j) => (j.Job || j.JobCode) === form.jobCode) || null;
    }, [form.jobCode, jobOptions]);

    if (loading) {
        return (
            <Backdrop open sx={{ color: '#fff', zIndex: 9999 }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }

    // ──────────────────────────────────────────────────────────────────
    //  Render
    // ──────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Header bar */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.25, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <IconButton onClick={() => navigate(returnTo)} edge="start" title={isFromJha ? 'Back to JHA' : 'Back to drafts'}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>Fall Arrest Rescue Plan</Typography>
                        <Chip
                            size="small"
                            label={form.status === STATUS_SUBMITTED ? 'Submitted' : 'Draft'}
                            sx={{
                                backgroundColor: form.status === STATUS_SUBMITTED ? '#4caf50' : '#9e9e9e',
                                color: 'white',
                            }}
                        />
                        {isFromJha && (
                            <Chip
                                size="small"
                                icon={<AssignmentIcon />}
                                label="From JHA"
                                onClick={() => navigate(returnTo)}
                                sx={{ cursor: 'pointer', backgroundColor: '#e3f2fd', color: '#1565c0' }}
                            />
                        )}
                    </Box>
                    <Stack direction="row" spacing={1}>
                        {form.status !== STATUS_SUBMITTED && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<ContentCopyIcon />}
                                onClick={openCopyPicker}
                                disabled={submitting}
                            >
                                Copy from
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            startIcon={<SendIcon />}
                            onClick={handleSubmit}
                            disabled={submitting}
                            sx={{ backgroundColor: '#1565c0', '&:hover': { backgroundColor: '#0d47a1' } }}
                        >
                            Submit
                        </Button>
                    </Stack>
                </Box>
            </Box>

            <Box sx={{ p: 1.5, flexGrow: 1, overflowY: 'auto' }}>

                {/* ─── Existing submitted FARP banner ─────────────────────
                     Show if there is already a SUBMITTED Fall Arrest Rescue Plan
                     for the same (jobCode + planDate). Lets the user view the
                     existing PDF instead of creating a duplicate.
                */}
                {existingSubmitted && form.status !== STATUS_SUBMITTED && (
                    <Alert
                        severity="info"
                        icon={<AssignmentIcon />}
                        action={
                            <Stack direction="row" spacing={1}>
                                <Button
                                    color="inherit"
                                    size="small"
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={viewExistingPdf}
                                >
                                    View PDF
                                </Button>
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() => setExistingSubmitted(null)}
                                >
                                    Dismiss
                                </Button>
                            </Stack>
                        }
                        sx={{ mb: 1.5 }}
                    >
                        <AlertTitle>Already filed for this job &amp; date</AlertTitle>
                        A Fall Arrest Rescue Plan was already submitted for{' '}
                        <strong>{existingSubmitted.planDate}</strong>
                        {existingSubmitted.submitterName ? ` by ${existingSubmitted.submitterName}` : ''}.
                        You can view the existing PDF or continue and submit a new one.
                    </Alert>
                )}

                {/* ─── 1. Job & Date ───────────────────────────────── */}
                <Section title="Job & Date">
                    <Grid container spacing={1.25}>
                        <Grid item xs={12} sm={8}>
                            <Autocomplete size="small"
                                options={jobOptions}
                                value={selectedJobOption}
                                getOptionLabel={(opt) =>
                                    opt ? `${opt.Job || opt.JobCode || ''} — ${opt.JobName || ''}` : ''
                                }
                                onChange={(_, val) => handleJobSelect(val)}
                                renderInput={(params) => <TextField size="small" {...params} label="Job" required />}
                                isOptionEqualToValue={(o, v) => (o.Job || o.JobCode) === (v?.Job || v?.JobCode)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField size="small"
                                label="Plan Date"
                                type="date"
                                value={form.planDate || ''}
                                onChange={(e) => updateField('planDate', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                required
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField size="small"
                                label="Customer"
                                value={form.customerName || ''}
                                InputProps={{ readOnly: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField size="small"
                                label="Location"
                                value={form.jobLocation || ''}
                                InputProps={{ readOnly: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextFieldWithMic
                                label="Work Description"
                                value={form.jobDescription}
                                onChange={(v) => updateField('jobDescription', v)}
                                autoGrow
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </Section>

                {/* ─── 2. Contacts ─────────────────────────────────── */}
                <Section title="Contacts">
                    <Stack spacing={1.5}>
                        {/* Rescuers — 2 источника: Weekly Roster (кто работает сегодня) ИЛИ All Employees */}
                        <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Rescuer(s)</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="contained"
                                        startIcon={<GroupIcon />}
                                        onClick={() => setRescuersRosterOpen(true)}
                                        sx={{ backgroundColor: '#1565c0', '&:hover': { backgroundColor: '#0d47a1' } }}
                                    >
                                        From Roster
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={(form.rescuers || []).filter(r => r?.name).length === 0 ? <AddIcon /> : <EditIcon />}
                                        onClick={() => setRescuersModalOpen(true)}
                                    >
                                        All Employees
                                    </Button>
                                </Stack>
                            </Stack>
                            {(form.rescuers || []).filter(r => r?.name).length === 0 ? (
                                <Typography variant="caption" color="text.secondary">None selected</Typography>
                            ) : (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {form.rescuers.filter(r => r?.name).map((r, i) => (
                                        <Chip
                                            key={`${r.empId || 'no-id'}-${i}-${r.name}`}
                                            label={`${r.name}${r.source === 'temp' ? ' (Temp)' : ''}`}
                                            onDelete={() => updateField('rescuers', form.rescuers.filter((_, idx) => idx !== i))}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>

                        {/* Competent Person */}
                        <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Competent Person</Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => setCompetentModalOpen(true)}
                                >
                                    Change
                                </Button>
                            </Stack>
                            {form.competentPerson?.name ? (
                                <Chip
                                    label={form.competentPerson.name}
                                    onDelete={() => updateField('competentPerson', { empId: null, name: '' })}
                                />
                            ) : (
                                <Typography variant="caption" color="text.secondary">Not selected</Typography>
                            )}
                        </Box>

                        {/* Emergency Contact — Pick из БД ИЛИ ручной ввод */}
                        <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Emergency Contact
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<PersonIcon />}
                                    onClick={() => setEmergencyModalOpen(true)}
                                >
                                    Pick from list
                                </Button>
                            </Stack>
                            <Grid container spacing={1.25}>
                                <Grid item xs={12} sm={7}>
                                    <TextField size="small"
                                        label="Name"
                                        value={form.emergencyContact?.name || ''}
                                        onChange={(e) => updateNested('emergencyContact', { name: e.target.value })}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <TextField size="small"
                                        label="Phone"
                                        value={form.emergencyContact?.phone || ''}
                                        onChange={(e) => updateNested('emergencyContact', { phone: e.target.value })}
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Stack>
                </Section>

                {/* ─── 3. Method of Contact ───────────────────────────── */}
                <Section title="Method of Contact">
                    <Grid container spacing={1}>
                        {/* Verbal / Face-to-Face */}
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel sx={{ ml: 0 }}
                                control={<Checkbox checked={!!form.methodOfContact?.faceToFace} onChange={() => toggleMethod('faceToFace')} />}
                                label="Verbal / Face-to-Face"
                            />
                        </Grid>

                        {/* Radio Channel */}
                        <Grid item xs={12} sm={6}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <FormControlLabel sx={{ ml: 0, flex: 1 }}
                                    control={<Checkbox checked={!!form.methodOfContact?.radio?.enabled} onChange={() => toggleMethod('radio')} />}
                                    label="Radio Channel"
                                />
                                {form.methodOfContact?.radio?.enabled && (
                                    <IconButton size="small" color="primary" title="Add channel" onClick={() => {
                                        const next = [...(form.methodOfContact.radio.channels || []), ''];
                                        updateMethodDetail('radio', 'channels', next);
                                    }}><AddIcon /></IconButton>
                                )}
                            </Stack>
                            {form.methodOfContact?.radio?.enabled && (
                                <Box sx={{ ml: 4 }}>
                                    {(form.methodOfContact?.radio?.channels || []).map((ch, idx) => (
                                        <Stack key={`radio-${idx}`} direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                            <TextField size="small" fullWidth
                                                placeholder="Channel"
                                                value={ch}
                                                onChange={(e) => {
                                                    const next = [...(form.methodOfContact.radio.channels || [])];
                                                    next[idx] = e.target.value;
                                                    updateMethodDetail('radio', 'channels', next);
                                                }}
                                            />
                                            <IconButton size="small" color="error" onClick={() => {
                                                const next = (form.methodOfContact.radio.channels || []).filter((_, i) => i !== idx);
                                                updateMethodDetail('radio', 'channels', next);
                                            }}><DeleteIcon fontSize="small" /></IconButton>
                                        </Stack>
                                    ))}
                                </Box>
                            )}
                        </Grid>

                        {/* Phone # */}
                        <Grid item xs={12} sm={6}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <FormControlLabel sx={{ ml: 0, flex: 1 }}
                                    control={<Checkbox checked={!!form.methodOfContact?.phone?.enabled} onChange={() => toggleMethod('phone')} />}
                                    label="Phone #"
                                />
                                {form.methodOfContact?.phone?.enabled && (
                                    <IconButton size="small" color="primary" title="Add phone" onClick={() => {
                                        const next = [...(form.methodOfContact.phone.numbers || []), ''];
                                        updateMethodDetail('phone', 'numbers', next);
                                    }}><AddIcon /></IconButton>
                                )}
                            </Stack>
                            {form.methodOfContact?.phone?.enabled && (
                                <Box sx={{ ml: 4 }}>
                                    {(form.methodOfContact?.phone?.numbers || []).map((ph, idx) => (
                                        <Stack key={`phone-${idx}`} direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                            <TextField size="small" fullWidth
                                                placeholder="Phone"
                                                value={ph}
                                                onChange={(e) => {
                                                    const next = [...(form.methodOfContact.phone.numbers || [])];
                                                    next[idx] = e.target.value;
                                                    updateMethodDetail('phone', 'numbers', next);
                                                }}
                                            />
                                            <IconButton size="small" color="error" onClick={() => {
                                                const next = (form.methodOfContact.phone.numbers || []).filter((_, i) => i !== idx);
                                                updateMethodDetail('phone', 'numbers', next);
                                            }}><DeleteIcon fontSize="small" /></IconButton>
                                        </Stack>
                                    ))}
                                </Box>
                            )}
                        </Grid>

                        {/* Custom methods — spans both cols */}
                        <Grid item xs={12}>
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                                    Other methods
                                </Typography>
                                <IconButton size="small" color="primary" title="Add method" onClick={() => {
                                    const next = [...(form.methodOfContact?.custom || []), { label: '', value: '' }];
                                    updateNested('methodOfContact', { custom: next });
                                }}><AddIcon /></IconButton>
                            </Stack>
                            {(form.methodOfContact?.custom || []).map((m, idx) => (
                                <Stack key={`custom-${idx}`} direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                    <TextField size="small"
                                        placeholder="Method name"
                                        value={m?.label || ''}
                                        onChange={(e) => {
                                            const next = [...(form.methodOfContact.custom || [])];
                                            next[idx] = { ...next[idx], label: e.target.value };
                                            updateNested('methodOfContact', { custom: next });
                                        }}
                                        sx={{ flex: '0 0 35%' }}
                                    />
                                    <TextField size="small" fullWidth
                                        placeholder="Detail (optional)"
                                        value={m?.value || ''}
                                        onChange={(e) => {
                                            const next = [...(form.methodOfContact.custom || [])];
                                            next[idx] = { ...next[idx], value: e.target.value };
                                            updateNested('methodOfContact', { custom: next });
                                        }}
                                    />
                                    <IconButton size="small" color="error" onClick={() => {
                                        const next = (form.methodOfContact.custom || []).filter((_, i) => i !== idx);
                                        updateNested('methodOfContact', { custom: next });
                                    }}><DeleteIcon fontSize="small" /></IconButton>
                                </Stack>
                            ))}
                        </Grid>
                    </Grid>
                </Section>

                {/* ─── 4. Rescue Equipment ───────────────────────────── */}
                <Section title="Rescue Equipment">
                    <Stack spacing={1.25}>
                        <Grid container spacing={1} alignItems="stretch">
                            {RESCUE_EQUIPMENT_LIST.map((item) => {
                                const selected = !!form.rescueEquipment?.[item.key];
                                return (
                                    <Grid item xs={12} sm={6} key={item.key} sx={{ display: 'flex' }}>
                                        <ToggleButton
                                            value={item.key}
                                            selected={selected}
                                            onChange={() => toggleEquipment(item.key)}
                                            fullWidth
                                            sx={{
                                                justifyContent: 'flex-start',
                                                textAlign: 'left',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontSize: '0.85rem',
                                                lineHeight: 1.25,
                                                py: 1, px: 1.25,
                                                minHeight: 44,
                                                width: '100%',
                                                border: '1px solid',
                                                borderColor: selected ? '#1565c0' : '#e0e0e0',
                                                backgroundColor: selected ? '#e3f2fd' : '#fff',
                                                color: selected ? '#0d47a1' : 'text.primary',
                                                '&:hover': {
                                                    backgroundColor: selected ? '#bbdefb' : '#f5f5f5',
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: '#e3f2fd',
                                                    '&:hover': { backgroundColor: '#bbdefb' },
                                                },
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                                                    {item.label}
                                                </Typography>
                                                {item.hint && (
                                                    <Typography variant="caption" sx={{
                                                        color: selected ? '#1565c0' : 'text.secondary',
                                                        display: 'block',
                                                        lineHeight: 1.2,
                                                        fontWeight: 400,
                                                    }}>
                                                        {item.hint}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </ToggleButton>
                                    </Grid>
                                );
                            })}
                        </Grid>

                        {/* Other — custom equipment (несколько, с описанием) */}
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                Other equipment
                            </Typography>
                            {(form.rescueEquipment?.customs || []).map((eq, idx) => (
                                <Stack key={`eq-${idx}`} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                                    <TextField size="small" fullWidth
                                        placeholder="Specify equipment"
                                        value={eq}
                                        onChange={(e) => {
                                            const next = [...(form.rescueEquipment.customs || [])];
                                            next[idx] = e.target.value;
                                            updateNested('rescueEquipment', { customs: next });
                                        }}
                                    />
                                    <IconButton color="error" onClick={() => {
                                        const next = (form.rescueEquipment.customs || []).filter((_, i) => i !== idx);
                                        updateNested('rescueEquipment', { customs: next });
                                    }}><DeleteIcon /></IconButton>
                                </Stack>
                            ))}
                            <Button size="small" startIcon={<AddIcon />} onClick={() => {
                                const next = [...(form.rescueEquipment?.customs || []), ''];
                                updateNested('rescueEquipment', { customs: next });
                            }}>Add equipment</Button>
                        </Box>
                    </Stack>
                </Section>

                {/* ─── 5. Equipment Location ─────────────────────────── */}
                <Section title="Location of Equipment">
                    <Stack spacing={1.25}>
                        <Grid container spacing={1} alignItems="stretch">
                            {EQUIPMENT_LOCATIONS.map((item) => {
                                const selected = !!form.equipmentLocation?.[item.key];
                                return (
                                    <Grid item xs={12} sm={6} key={item.key} sx={{ display: 'flex' }}>
                                        <ToggleButton
                                            value={item.key}
                                            selected={selected}
                                            onChange={() => toggleLocation(item.key)}
                                            fullWidth
                                            sx={{
                                                justifyContent: 'flex-start',
                                                textAlign: 'left',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontSize: '0.85rem',
                                                py: 1, px: 1.25,
                                                minHeight: 44,
                                                width: '100%',
                                                border: '1px solid',
                                                borderColor: selected ? '#1565c0' : '#e0e0e0',
                                                backgroundColor: selected ? '#e3f2fd' : '#fff',
                                                color: selected ? '#0d47a1' : 'text.primary',
                                                '&:hover': { backgroundColor: selected ? '#bbdefb' : '#f5f5f5' },
                                                '&.Mui-selected': {
                                                    backgroundColor: '#e3f2fd',
                                                    '&:hover': { backgroundColor: '#bbdefb' },
                                                },
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                                                    {item.label}
                                                </Typography>
                                                {item.hint && (
                                                    <Typography variant="caption" sx={{
                                                        color: selected ? '#1565c0' : 'text.secondary',
                                                        display: 'block',
                                                        lineHeight: 1.2,
                                                        fontWeight: 400,
                                                    }}>
                                                        {item.hint}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </ToggleButton>
                                    </Grid>
                                );
                            })}
                        </Grid>

                        {/* Custom locations — несколько */}
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                Other locations
                            </Typography>
                            {(form.equipmentLocation?.customs || []).map((loc, idx) => (
                                <Stack key={`loc-${idx}`} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                                    <TextField size="small" fullWidth
                                        placeholder="Specify location"
                                        value={loc}
                                        onChange={(e) => {
                                            const next = [...(form.equipmentLocation.customs || [])];
                                            next[idx] = e.target.value;
                                            updateNested('equipmentLocation', { customs: next });
                                        }}
                                    />
                                    <IconButton color="error" onClick={() => {
                                        const next = (form.equipmentLocation.customs || []).filter((_, i) => i !== idx);
                                        updateNested('equipmentLocation', { customs: next });
                                    }}><DeleteIcon /></IconButton>
                                </Stack>
                            ))}
                            <Button size="small" startIcon={<AddIcon />} onClick={() => {
                                const next = [...(form.equipmentLocation?.customs || []), ''];
                                updateNested('equipmentLocation', { customs: next });
                            }}>Add location</Button>
                        </Box>
                    </Stack>
                </Section>

                {/* ─── 6. Critical Rescue Factors ────────────────────── */}
                <Section title="Critical Rescue Factors">
                    <Stack spacing={1}>
                        <TextFieldWithMic
                            label="Anchor Point (if Applicable)"
                            value={form.anchorPoint}
                            onChange={(v) => updateField('anchorPoint', v)}
                            autoGrow
                        />
                        <TextFieldWithMic
                            label="Landing Area"
                            value={form.landingArea}
                            onChange={(v) => updateField('landingArea', v)}
                            autoGrow
                        />
                        <TextFieldWithMic
                            label="Rescue Obstructions / Hazards"
                            value={form.obstructionsHazards}
                            onChange={(v) => updateField('obstructionsHazards', v)}
                            autoGrow
                        />
                    </Stack>
                </Section>

                {/* ─── 7. Verification Checklist ──────────────────────── */}
                <Section title="Verification Checklist">
                    <Stack spacing={0.75}>
                        {(form.checklist || []).map((item) => (
                            <Paper key={item.id} variant="outlined" sx={{ p: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.3 }}>
                                        <strong>{item.id}.</strong> {item.question}
                                    </Typography>
                                    <ToggleButtonGroup
                                        size="small"
                                        exclusive
                                        value={item.answer}
                                        onChange={(_, val) => updateChecklistAnswer(item.id, val)}
                                    >
                                        <ToggleButton value="yes" color="success" sx={{ py: 0.25, px: 1.5 }}>YES</ToggleButton>
                                        <ToggleButton value="no" color="error" sx={{ py: 0.25, px: 1.5 }}>NO</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                                <TextFieldWithMic
                                    singleLine
                                    placeholder="Comment (optional)"
                                    value={item.comment || ''}
                                    onChange={(v) => updateChecklistComment(item.id, v)}
                                    size="small"
                                />
                            </Paper>
                        ))}
                    </Stack>
                </Section>

                {/* ─── 8. Pre-Work Tasks (Rescue Prep) ──────────────── */}
                <Section title="Pre-Work Tasks (Rescue Prep)">
                    <Stack spacing={1}>
                        {(form.preWorkTasks || []).map((t, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Typography variant="body2" sx={{ pt: 1.5, minWidth: 24 }}>{i + 1}.</Typography>
                                <Box sx={{ flex: 1 }}>
                                    <TextFieldWithMic
                                        value={t.text}
                                        onChange={(v) => updatePreWorkTask(i, v)}
                                        placeholder={`Task ${i + 1}`}
                                        singleLine
                                    />
                                </Box>
                                <IconButton color="error" onClick={() => removePreWorkTask(i)}>
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ))}
                        <Button startIcon={<AddIcon />} onClick={addPreWorkTask} variant="outlined" size="small">
                            Add task
                        </Button>
                    </Stack>
                </Section>

                {/* ─── 9. Response Procedure ────────────────────────── */}
                <Section title="Response Procedure">
                    <Stack spacing={1}>
                        {(form.responseProcedure || []).map((r, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Typography variant="body2" sx={{ pt: 1.5, minWidth: 24 }}>{i + 1}.</Typography>
                                <Box sx={{ flex: 1 }}>
                                    <TextFieldWithMic
                                        value={r.text}
                                        onChange={(v) => updateResponseStep(i, v)}
                                        placeholder={r.isDefault ? `Default step ${i + 1}` : `Step ${i + 1}`}
                                        singleLine
                                    />
                                </Box>
                                <IconButton color="error" onClick={() => removeResponseStep(i)}>
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ))}
                        <Button startIcon={<AddIcon />} onClick={addResponseStep} variant="outlined" size="small">
                            Add step
                        </Button>
                    </Stack>
                </Section>

                {/* ─── 10. Submitter / Signature — одна row ─────────────── */}
                <Section title="Submitter Acknowledgment">
                    <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth size="small"
                                label="Submitter *"
                                value={form.submitterName || ''}
                                onClick={() => setSubmitterModalOpen(true)}
                                InputProps={{
                                    readOnly: true,
                                    style: { cursor: 'pointer' },
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSubmitterModalOpen(true)}>
                                                <PersonSearchIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box
                                onClick={() => setSignatureOpen(true)}
                                sx={{
                                    border: '1px solid #ccc',
                                    borderRadius: 1,
                                    height: 40,
                                    backgroundColor: '#fafafa',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    px: 1,
                                }}
                            >
                                {form.submitterSignature ? (
                                    <img
                                        src={form.submitterSignature}
                                        alt="Submitter Signature"
                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                    />
                                ) : (
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                                        <DriveFileRenameOutlineIcon fontSize="small" />
                                        <Typography variant="body2">Tap to sign *</Typography>
                                    </Stack>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Section>

                {/* Bottom submit */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<SendIcon />}
                        onClick={handleSubmit}
                        disabled={submitting}
                        sx={{ backgroundColor: '#1565c0', '&:hover': { backgroundColor: '#0d47a1' } }}
                    >
                        Submit
                    </Button>
                </Box>
            </Box>

            {/* Modals */}
            <SignatureModal
                allowAuto
                open={signatureOpen}
                onClose={() => setSignatureOpen(false)}
                onSave={handleSignatureSave}
                existingSignature={form.submitterSignature || ''}
                employeeName={form.submitterName || userFullName}
            />

            {rescuersModalOpen && (
                <SelectEmployeesAndTempsModal
                    open={rescuersModalOpen}
                    onClose={() => setRescuersModalOpen(false)}
                    onSelect={handleRescuersSelected}
                    alreadySelected={(form.rescuers || [])
                        .filter(r => r?.name)
                        .map((r) => ({
                            _type: r.source === 'temp' ? 'temp' : 'employee',
                            FullName: r.name,
                            employeeId: r.empId,
                        }))}
                />
            )}

            {rescuersRosterOpen && (
                <SelectFromWeeklyRosterModal
                    open={rescuersRosterOpen}
                    onClose={() => setRescuersRosterOpen(false)}
                    onSelect={handleRescuersSelected}
                    alreadySelected={(form.rescuers || [])
                        .filter(r => r?.name)
                        .map((r) => ({
                            _type: r.source === 'temp' ? 'temp' : 'employee',
                            FullName: r.name,
                            employeeId: r.empId,
                        }))}
                />
            )}

            {competentModalOpen && (
                <SelectEmployeesAndTempsModal
                    open={competentModalOpen}
                    onClose={() => setCompetentModalOpen(false)}
                    onSelect={handleCompetentSelected}
                    singleSelect
                    employeesOnly
                    alreadySelected={form.competentPerson?.name ? [{
                        _type: 'employee',
                        FullName: form.competentPerson.name,
                        employeeId: form.competentPerson.empId,
                    }] : []}
                />
            )}

            {emergencyModalOpen && (
                <SelectEmployeesAndTempsModal
                    open={emergencyModalOpen}
                    onClose={() => setEmergencyModalOpen(false)}
                    onSelect={handleEmergencyContactSelected}
                    singleSelect
                    employeesOnly
                    alreadySelected={form.emergencyContact?.name ? [{
                        _type: 'employee',
                        FullName: form.emergencyContact.name,
                    }] : []}
                />
            )}

            {submitterModalOpen && (
                <SelectEmployeesAndTempsModal
                    open={submitterModalOpen}
                    onClose={() => setSubmitterModalOpen(false)}
                    onSelect={handleSubmitterSelected}
                    singleSelect
                    employeesOnly
                    alreadySelected={form.submitterName ? [{
                        _type: 'employee',
                        FullName: form.submitterName,
                    }] : []}
                />
            )}

            {/* ─── Copy-from picker modal ─────────────────────────────── */}
            <Dialog open={copyModalOpen} onClose={() => setCopyModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Copy from previous</DialogTitle>
                <DialogContent dividers>
                    {copySources.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                            No previous submitted plans on this device.
                        </Typography>
                    ) : (
                        <>
                            <TextField
                                autoFocus
                                fullWidth
                                size="small"
                                placeholder="Search by job, date, name…"
                                value={copySearch}
                                onChange={(e) => setCopySearch(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                                }}
                                sx={{ mb: 1.5 }}
                            />
                            <List dense disablePadding sx={{ maxHeight: 380, overflowY: 'auto' }}>
                                {filteredCopySources.map((s) => {
                                    const sameJob = form.jobCode && s.jobCode === form.jobCode;
                                    return (
                                        <ListItemButton
                                            key={s.uuid}
                                            onClick={() => applyCopyFrom(s)}
                                            sx={{
                                                borderLeft: sameJob ? '3px solid #1565c0' : '3px solid transparent',
                                                mb: 0.5, borderRadius: 1,
                                            }}
                                        >
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Typography variant="subtitle2">
                                                        {s.jobCode || '(no job)'}
                                                        {s.jobName && <Typography component="span" variant="body2" sx={{ ml: 1 }}>— {s.jobName}</Typography>}
                                                    </Typography>
                                                    {sameJob && <Chip label="Same job" size="small" color="primary" sx={{ height: 18 }} />}
                                                </Box>
                                                <Typography variant="caption" color="text.secondary" component="div">
                                                    {s.planDate && <>Date: {s.planDate} · </>}
                                                    {s.submitterName && <>By: {s.submitterName}</>}
                                                </Typography>
                                            </Box>
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCopyModalOpen(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* ─── PDF preview modal — для existingSubmitted banner ─────── */}
            <PdfPreviewModal
                open={pdfModalOpen}
                onClose={handleClosePdfModal}
                pdfBlob={pdfBlob}
                filename={`FARP_${existingSubmitted?.planDate || ''}.pdf`}
                loading={pdfLoading}
            />

            <Backdrop open={submitting} sx={{ color: '#fff', zIndex: 9999 }}>
                <CircularProgress color="inherit" />
                <Typography sx={{ ml: 2 }}>Submitting…</Typography>
            </Backdrop>
        </Box>
    );
}
